import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { supabase } from '../config/supabase';
import { ServiceCategory } from '../types';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [popularServices, setPopularServices] = useState<ServiceCategory[]>([]);

  useEffect(() => {
    fetchPopularServices();
  }, []);

  const fetchPopularServices = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .order('name')
        .limit(5);

      if (error) throw error;
      setPopularServices(data || []);
    } catch (error) {
      console.error('Error fetching popular services:', error);
    }
  };

  const footerLinks = {
    services: popularServices.length > 0 
      ? [
          ...popularServices.map(service => ({
            name: service.name,
            href: `/service/${service.slug}`
          })),
          { name: 'Tous les services', href: '/#services' }
        ]
      : [
          { name: 'Garde d\'enfants', href: '/service/garde-enfants' },
          { name: 'Ménage', href: '/service/menage' },
          { name: 'Jardinage', href: '/service/jardinage' },
          { name: 'Bricolage', href: '/service/bricolage' },
          { name: 'Tous les services', href: '/#services' }
        ],
    company: [
      { name: 'À propos', href: '/about' },
      { name: 'FAQ', href: '/faq' },
      { name: 'Devenir prestataire', href: '/register' },
      { name: 'Contact', href: '/contact' }
    ],
    legal: [
      { name: 'Conditions d\'utilisation', href: '/terms' },
      { name: 'Politique de confidentialité', href: '/privacy' },
      { name: 'Politique des cookies', href: '/cookies' },
      { name: 'Mentions légales', href: '/legal' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: '#' },
    { name: 'Twitter', icon: Twitter, href: '#' },
    { name: 'Instagram', icon: Instagram, href: '#' },
    { name: 'LinkedIn', icon: Linkedin, href: '#' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-gold">
                <img
                  src="/images/casap192.png"
                  alt="CasaPresta"
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  CasaPresta
                </h3>
                <p className="text-xs text-gray-400 font-medium tracking-wide">
                  Services à domicile
                </p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              La plateforme de référence pour vos services à domicile en Côte d'Ivoire. 
              Connectez-vous avec des professionnels qualifiés et vérifiés.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center text-gray-300">
                <Mail className="h-4 w-4 mr-3 text-primary-400" />
                <span className="text-sm">support@casapresta.online</span>
              </div> 
              <div className="flex items-center text-gray-300">
                <Phone className="h-4 w-4 mr-3 text-primary-400" />
                <span className="text-sm">+225 05 44 86 77 55</span>
              </div> 
              <div className="flex items-center text-gray-300">
                <Phone className="h-4 w-4 mr-3 text-primary-400" />
                <span className="text-sm">+225 01 52 85 02 16</span>
              </div>
               <div className="flex items-center text-gray-300">
                <Phone className="h-4 w-4 mr-3 text-primary-400" />
                <span className="text-sm">+225 01 52 77 89 04</span>
              </div>
              <div className="flex items-center text-gray-300">
                <MapPin className="h-4 w-4 mr-3 text-primary-400" />
                <span className="text-sm">Abidjan, Côte d'Ivoire</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-script font-semibold mb-6 text-white">
              Services populaires
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-script font-semibold mb-6 text-white">
              Entreprise
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Newsletter */}
          <div>
            <h4 className="text-lg font-script font-semibold mb-6 text-white">
              Informations légales
            </h4>
            <ul className="space-y-3 mb-8">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Newsletter */}
            <div>
              <h5 className="text-sm font-script font-semibold mb-3 text-white">
                Newsletter
              </h5>
              <p className="text-xs text-gray-400 mb-4">
                Recevez nos dernières actualités et offres spéciales
              </p>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Votre email"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-l-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-primary-500"
                />
                <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-r-lg transition-colors duration-200">
                  <Mail className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="flex items-center text-gray-400 text-sm">
              <span>© {currentYear} CasaPresta - Universal Digital CasaPresta Group. Tous droits réservés.</span>
             
            
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm mr-2">Suivez-nous :</span>
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                  aria-label={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="flex flex-wrap justify-center items-center space-x-8 text-xs text-gray-500">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span>Prestataires vérifiés</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                <span>Paiements sécurisés</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                <span>Support 24/7</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                <span>Satisfaction garantie</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;